/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/custom/AddressSection.tsx
 * Release: 2.1.0 - Aggiunto calcolo tempo di percorrenza
 * Descrizione: Sezione completa per la gestione dell'indirizzo con autocomplete Google Maps e calcolo tempo di percorrenza
 */

import React, { useState, useCallback, useEffect } from 'react';
import { MapPin, Clock, Car } from 'lucide-react';
import { GoogleMapsAddressAutocomplete } from './GoogleMapsAddressAutocomplete';
import { InteractiveMap } from './InteractiveMap';
import { googleMapsService } from '../../../services/googleMapsService';

// Indirizzo di default (sede Coronatek) - Sincronizzato con config globale
const DEFAULT_ADDRESS = {
  lat: 37.5189, // Coordinate di Misterbianco - Via Salvatore Averna, 3
  lng: 15.0094,
  address: 'Sede Coronatek, Via Salvatore Averna, 3, Misterbianco (CT)'
};

interface TravelTimeData {
  duration: string;
  distance: string;
  loading: boolean;
  error: string | null;
}

interface AddressSectionProps {
  value?: any;
  onChange?: (value: any) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  helpText?: string;
  hasError?: boolean;
  errorMessage?: string;
  onFieldsUpdate?: (fields: Record<string, any>) => void;
}

interface AddressData {
  search: string;
  via: string;
  numeroCivico: string;
  citta: string;
  cap: string;
  provincia: string;
  paese: string;
  lat?: number;
  lng?: number;
}

// Funzione per capitalizzare ogni parola
function capitalizeWords(str: string) {
  return str.replace(/\b\w+/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

// 🚨 DEBUG: Funzione per sanitizzare valori null/undefined prima di passarli agli input HTML
function sanitizeInputValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}

// Componente per il calcolo del tempo di percorrenza
const TravelTimeCalculator: React.FC<{ 
  originLat?: number; 
  originLng?: number; 
  disabled?: boolean;
}> = ({ originLat, originLng, disabled }) => {
  const [travelTime, setTravelTime] = useState<TravelTimeData>({
    duration: '',
    distance: '',
    loading: false,
    error: null
  });

  // Orari di lavoro ora gestiti dal servizio centralizzato

  const calculateTravelTime = useCallback(async () => {
    if (!originLat || !originLng || disabled) {
      setTravelTime({
        duration: '',
        distance: '',
        loading: false,
        error: null
      });
      return;
    }

    console.log('[TravelTimeCalculator] 🚗 Calcolo con servizio centralizzato:', { originLat, originLng });
    setTravelTime(prev => ({ ...prev, loading: true, error: null }));

    try {
      const result = await googleMapsService.getTravelTime(originLat, originLng);
      
      if (result) {
        console.log('[TravelTimeCalculator] ✅ Tempi ottenuti dal servizio centralizzato:', result);
        setTravelTime({
          duration: result.duration,
          distance: result.distance,
          loading: false,
          error: null
        });
      } else {
        console.error('[TravelTimeCalculator] ❌ Nessun risultato dal servizio centralizzato');
        setTravelTime({
          duration: '',
          distance: '',
          loading: false,
          error: 'Impossibile calcolare il percorso'
        });
      }
    } catch (error) {
      console.error('[TravelTimeCalculator] Errore servizio centralizzato:', error);
      setTravelTime({
        duration: '',
        distance: '',
        loading: false,
        error: 'Errore nel calcolo del percorso'
      });
    }
  }, [originLat, originLng, disabled]);

  useEffect(() => {
    // Calcola il tempo quando cambiano le coordinate
    console.log('[TravelTimeCalculator] useEffect - coordinate:', { originLat, originLng });
    if (originLat && originLng) {
      console.log('[TravelTimeCalculator] Avvio calcolo con timer da 1 secondo');
      const timer = setTimeout(calculateTravelTime, 1000); // Delay per evitare troppe chiamate
      return () => clearTimeout(timer);
    }
  }, [originLat, originLng, calculateTravelTime]);


  if (!originLat || !originLng) {
    console.log('[TravelTimeCalculator] Coordinate non disponibili, componente nascosto:', { originLat, originLng });
    return (
      <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
        <div className="flex items-center gap-2 mb-2">
          <Car className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-zinc-500">
            Inserisci un indirizzo valido per calcolare il tempo di percorrenza
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
      <div className="flex items-center gap-2 mb-2">
        <Car className="w-4 h-4 text-green-400" />
        <span className="text-sm font-medium text-zinc-300">
          Tempo medio di percorrenza verso {DEFAULT_ADDRESS.address}
        </span>
      </div>
      <div className="text-xs text-zinc-500 mb-2">
        Media calcolata durante le ore di lavoro (8:00-10:00, 14:00-17:00) con traffico
      </div>
      
      {travelTime.loading ? (
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-400"></div>
          Calcolando media oraria...
        </div>
      ) : travelTime.error ? (
        <div className="text-sm text-red-400">
          {travelTime.error}
        </div>
      ) : travelTime.duration ? (
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1 text-zinc-300">
            <Clock className="w-4 h-4 text-green-400" />
            <span>{travelTime.duration}</span>
          </div>
          <div className="flex items-center gap-1 text-zinc-400">
            <MapPin className="w-4 h-4" />
            <span>{travelTime.distance}</span>
          </div>
        </div>
      ) : (
        <div className="text-sm text-zinc-500">
          Inserisci un indirizzo valido per calcolare il tempo di percorrenza
        </div>
      )}
    </div>
  );
};

// Cache e gestione API ora centralizzate nel googleMapsService

const AddressSection: React.FC<AddressSectionProps> = ({
  value = {},
  onChange,
  onBlur,
  disabled = false,
  placeholder = "Cerca indirizzo...",
  helpText,
  hasError = false,
  errorMessage
}) => {
  // --- FIX DEFINITIVO: Assicura che 'value' sia sempre un oggetto ---
  let parsedValue: Partial<AddressData> = {}; // Specifica il tipo qui
  if (typeof value === 'string') {
    try {
      parsedValue = JSON.parse(value);
    } catch (e) {
      console.error("AddressSection: Errore parsing 'value' da stringa JSON", e);
      parsedValue = {}; // In caso di errore, usa un oggetto vuoto
    }
  } else if (typeof value === 'object' && value !== null) {
    parsedValue = value;
  }
  // Da qui in poi, usiamo SEMPRE 'parsedValue' invece di 'value'

  console.log('🏠 [AddressSection] PROPS RICEVUTE (dopo parsing):', parsedValue);
  const [addressData, setAddressData] = useState<AddressData>({
    search: '',
    via: parsedValue?.via || '',
    numeroCivico: parsedValue?.numeroCivico || '',
    citta: parsedValue?.citta || '',
    cap: parsedValue?.cap || '',
    provincia: parsedValue?.provincia || '',
    paese: parsedValue?.paese || 'Italia',
    lat: parsedValue?.lat && parsedValue.lat !== null ? Number(parsedValue.lat) : undefined,
    lng: parsedValue?.lng && parsedValue.lng !== null ? Number(parsedValue.lng) : undefined
  });

  const [fieldErrors, setFieldErrors] = useState<{[key: string]: boolean}>({});

  // ✨ useEffect per ricalcolare i tempi quando si caricano dati esistenti con coordinate
  useEffect(() => {
    // Delay per assicurarsi che il componente TravelTimeCalculator sia completamente montato
    if (addressData.lat && addressData.lng) {
      console.log('[AddressSection] 🚗 Dati con coordinate caricati dal DB, forzo ricalcolo tempi:', {
        lat: addressData.lat, 
        lng: addressData.lng
      });
      const timer = setTimeout(() => {
        // Forza un re-render del TravelTimeCalculator scatenando un cambiamento di stato minimo
        // Questo è un workaround per forzare il ricalcolo dei tempi
        setAddressData(prev => ({ ...prev }));
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [addressData.lat, addressData.lng]);

  // Effetto per aggiornare addressData quando cambia value
  useEffect(() => {
    // La logica di parsing è già stata fatta sopra, qui usiamo 'parsedValue'
    if (parsedValue && Object.keys(parsedValue).length > 0) {
      const newData = {
        search: parsedValue.search || '',
        via: parsedValue.via || '',
        numeroCivico: parsedValue.numeroCivico || '',
        citta: parsedValue.citta || '',
        cap: parsedValue.cap || '',
        provincia: parsedValue.provincia || '',
        paese: parsedValue.paese || 'Italia',
        lat: parsedValue.lat && parsedValue.lat !== null ? Number(parsedValue.lat) : undefined,
        lng: parsedValue.lng && parsedValue.lng !== null ? Number(parsedValue.lng) : undefined
      };

      // Aggiorna solo se i dati sono diversi
      if (JSON.stringify(newData) !== JSON.stringify(addressData)) {
        setAddressData(newData);

        // Se abbiamo via e città ma NON coordinate, geocodifica automaticamente
        if (newData.via && newData.citta && !newData.lat && !newData.lng) {
          const addressString = `${newData.via} ${newData.numeroCivico || ''}, ${newData.cap || ''} ${newData.citta} ${newData.provincia ? `(${newData.provincia})` : ''}, ${newData.paese}`;
          console.log('[AddressSection] 🗺️ Geocodifica automatica per dati iniziali:', addressString);

          // Geocodifica con delay per permettere al componente di montarsi
          setTimeout(async () => {
            try {
              const result = await googleMapsService.geocodeAddress(addressString);
              if (result && result.lat && result.lng) {
                console.log('[AddressSection] ✅ Geocodifica iniziale completata:', result);
                setAddressData(prev => ({
                  ...prev,
                  lat: result.lat,
                  lng: result.lng
                }));
                // Aggiorna la mappa
                if (window.updateMapLocation && isFinite(result.lat) && isFinite(result.lng)) {
                  window.updateMapLocation(result.lat, result.lng);
                }
              }
            } catch (error) {
              console.error('[AddressSection] ❌ Errore geocodifica iniziale:', error);
            }
          }, 1000);
        }
      }
    }
  }, [parsedValue]); // Dipende dal valore parsato

  // Validazione: se non si usa il campo ricerca, tutti i campi base sono obbligatori
  const validateFields = useCallback((data: AddressData) => {
    const errors: {[key: string]: boolean} = {};
    
    // 🚨 DEBUG: Sanitizza valori null prima della validazione
    const sanitizedData = {
      search: data.search || '',
      via: data.via || '',
      numeroCivico: data.numeroCivico || '',
      citta: data.citta || '',
      cap: data.cap || '',
      provincia: data.provincia || '',
      paese: data.paese || 'Italia',
      lat: data.lat || undefined,
      lng: data.lng || undefined
    };
    
    console.log('[AddressSection] validateFields - Dati sanitizzati:', sanitizedData);
    
    // Se il campo ricerca è compilato, la sezione è valida
    if (sanitizedData.search.trim()) {
      setFieldErrors({});
      return true;
    }
    
    // Altrimenti, valida i campi manuali obbligatori
    if (!sanitizedData.via.trim()) errors.via = true;
    if (!sanitizedData.citta.trim()) errors.citta = true;
    if (!sanitizedData.cap.trim()) errors.cap = true;
    if (!sanitizedData.provincia.trim()) errors.provincia = true;
    
    console.log('[AddressSection] validateFields - Errori trovati:', errors);
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, []);

  const updateAddressData = useCallback((updates: Partial<AddressData>) => {
    let newData = { ...addressData, ...updates };

    // Formattazione: via, città, paese capitalizzate; provincia sempre maiuscola
    if ('via' in updates) newData.via = capitalizeWords(updates.via || '');
    if ('citta' in updates) newData.citta = capitalizeWords(updates.citta || '');
    if ('paese' in updates) newData.paese = capitalizeWords(updates.paese || '');
    if ('provincia' in updates) newData.provincia = (updates.provincia || '').toUpperCase();
    
    // 🚨 DEBUG: Sanitizza valori null per lat e lng
    if ('lat' in updates && updates.lat === null) newData.lat = undefined;
    if ('lng' in updates && updates.lng === null) newData.lng = undefined;

    setAddressData(newData);
    validateFields(newData);
    
    if (onChange) {
      onChange(newData);
    }

    // Se tutti i campi manuali sono validi e non c'è ricerca, geocodifica con servizio centralizzato
    if (!newData.search && newData.via && newData.citta && newData.cap && newData.provincia) {
      const addressString = `${newData.via} ${newData.numeroCivico || ''}, ${newData.cap} ${newData.citta} (${newData.provincia}), ${newData.paese}`;
      
      // Debouncing per evitare troppe chiamate API durante la digitazione
      const geocodeDebounced = debounce(async (addressToGeocode: string) => {
        console.log('[AddressSection] 🔍 Geocodificazione con servizio centralizzato:', addressToGeocode);
        
        try {
          const result = await googleMapsService.geocodeAddress(addressToGeocode);
          
          if (result) {
            const sanitizedLat = result.lat || undefined;
            const sanitizedLng = result.lng || undefined;
            
            console.log('[AddressSection] ✅ Geocodificazione completata dal servizio centralizzato');
            setAddressData(prev => ({ ...prev, lat: sanitizedLat, lng: sanitizedLng }));
            if (window.updateMapLocation && sanitizedLat && sanitizedLng && 
                isFinite(sanitizedLat) && isFinite(sanitizedLng)) {
              window.updateMapLocation(sanitizedLat, sanitizedLng);
            }
          } else {
            console.warn('[AddressSection] ⚠️ Geocodificazione fallita dal servizio centralizzato');
          }
        } catch (error) {
          console.error('[AddressSection] ❌ Errore geocodificazione servizio centralizzato:', error);
        }
      }, 1500); // 1.5 secondi di debounce
      
      geocodeDebounced(addressString);
    }
  }, [addressData, onChange, validateFields]);

  const handleSearchChange = useCallback((search: string) => {
    // 🚨 DEBUG: Sanitizza valori null prima di aggiornare
    const sanitizedSearch = search === null ? '' : search;
    console.log(`[AddressSection] handleSearchChange: "${sanitizedSearch}" (originale: ${search})`);
    updateAddressData({ search: sanitizedSearch });
  }, [updateAddressData]);

  const handlePlaceSelect = useCallback((place: any, event?: Event) => {
    console.log('[AddressSection] handlePlaceSelect chiamato con:', place);
    console.log('[AddressSection] Stack trace place select:', new Error().stack);
    
    // 🚨 PREVENIRE eventi che potrebbero causare redirect
    if (event) {
      event.preventDefault?.();
      event.stopPropagation?.();
      event.stopImmediatePropagation?.();
    }
    
    if (!place) {
      console.log('[AddressSection] Place è null o undefined');
      return;
    }
    
    const addressComponents = place.address_components;
    const geometry = place.geometry;
    const formattedAddress = place.formatted_address;
    
    console.log('[AddressSection] Dati place ricevuti:');
    console.log('- address_components:', addressComponents);
    console.log('- geometry:', geometry);
    console.log('- formatted_address:', formattedAddress);
    
    if (!addressComponents) {
      console.log('[AddressSection] address_components mancanti');
      return;
    }

    // Estrai i componenti dell'indirizzo
    let via = '';
    let numeroCivico = '';
    let citta = '';
    let cap = '';
    let provincia = '';
    let paese = 'Italia';

    addressComponents.forEach((component: any, index: number) => {
      console.log(`[AddressSection] Component ${index}:`, component);
      const types = component.types;
      
      if (types.includes('route')) {
        via = component.long_name || '';
        console.log('[AddressSection] Via estratta:', via);
      } else if (types.includes('street_number')) {
        numeroCivico = component.long_name || '';
        console.log('[AddressSection] Numero civico estratto:', numeroCivico);
      } else if (types.includes('locality') || types.includes('administrative_area_level_3')) {
        citta = component.long_name || '';
        console.log('[AddressSection] Città estratta:', citta);
      } else if (types.includes('postal_code')) {
        cap = component.long_name || '';
        console.log('[AddressSection] CAP estratto:', cap);
      } else if (types.includes('administrative_area_level_2')) {
        provincia = component.short_name || '';
        console.log('[AddressSection] Provincia estratta:', provincia);
      } else if (types.includes('country')) {
        paese = component.long_name || '';
        console.log('[AddressSection] Paese estratto:', paese);
      }
    });

    // Ottieni le coordinate
    let lat, lng;
    if (geometry?.location) {
      if (typeof geometry.location.lat === 'function') {
        // Formato Google Maps standard
        lat = geometry.location.lat();
        lng = geometry.location.lng();
      } else if (typeof geometry.location.lat === 'number') {
        // Formato diretto
        lat = geometry.location.lat;
        lng = geometry.location.lng;
      }
    }

    console.log('[AddressSection] Coordinate estratte:', { lat, lng });
    console.log('[AddressSection] Dati estratti:', { via, numeroCivico, citta, cap, provincia, paese, lat, lng });

    // Aggiorna tutti i campi e mantiene la ricerca con l'indirizzo formattato
    const newData = {
      search: formattedAddress || '', // Mantiene l'indirizzo formattato nel campo di ricerca
      via,
      numeroCivico,
      citta,
      cap,
      provincia,
      paese,
      lat: lat || undefined,
      lng: lng || undefined
    };
    
    // Sanitizzazione rigorosa dei dati per evitare problemi
    const sanitizedData = {
      search: String(newData.search || ''),
      via: String(newData.via || ''),
      numeroCivico: String(newData.numeroCivico || ''),
      citta: String(newData.citta || ''),
      cap: String(newData.cap || ''),
      provincia: String(newData.provincia || ''),
      paese: String(newData.paese || 'Italia'),
      lat: typeof newData.lat === 'number' && isFinite(newData.lat) ? newData.lat : undefined,
      lng: typeof newData.lng === 'number' && isFinite(newData.lng) ? newData.lng : undefined
    };
    
    updateAddressData(sanitizedData);

    // Aggiorna la mappa
    if (lat && lng && isFinite(lat) && isFinite(lng)) {
      console.log('[AddressSection] Aggiorno mappa con coordinate valide:', lat, lng);
      if (window.updateMapLocation) {
        window.updateMapLocation(lat, lng);
        console.log('[AddressSection] updateMapLocation chiamata');
      } else {
        console.log('[AddressSection] window.updateMapLocation non disponibile');
        // Retry dopo un breve delay
        setTimeout(() => {
          if (window.updateMapLocation) {
            window.updateMapLocation(lat, lng);
            console.log('[AddressSection] updateMapLocation chiamata (retry)');
          }
        }, 500);
      }
    } else {
      console.log('[AddressSection] Coordinate non disponibili');
    }
    
    console.log('[AddressSection] Dati per onChange:', newData);
  }, [updateAddressData]);

  const handleFieldChange = useCallback((field: keyof AddressData, value: string) => {
    // 🚨 DEBUG: Sanitizza valori null prima di aggiornare
    const sanitizedValue = value === null ? '' : value;
    console.log(`[AddressSection] handleFieldChange: ${field} = "${sanitizedValue}" (originale: ${value})`);
    updateAddressData({ [field]: sanitizedValue });
  }, [updateAddressData]);

  const handleFieldBlur = useCallback(() => {
    // 🚨 DEBUG: Sanitizza valori null prima della validazione
    const sanitizedData = {
      ...addressData,
      search: addressData.search || '',
      via: addressData.via || '',
      numeroCivico: addressData.numeroCivico || '',
      citta: addressData.citta || '',
      cap: addressData.cap || '',
      provincia: addressData.provincia || '',
      paese: addressData.paese || 'Italia',
      lat: addressData.lat || undefined,
      lng: addressData.lng || undefined
    };
    
    console.log('[AddressSection] handleFieldBlur - Dati sanitizzati:', sanitizedData);
    validateFields(sanitizedData);
    if (onBlur) onBlur();
  }, [addressData, validateFields, onBlur]);

  return (
    <div className="p-4">
      <div className="space-y-4">
        {/* Campo di ricerca Google Maps */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-zinc-300">
            <MapPin className="w-4 h-4" />
            Cerca Indirizzo
          </label>
                          <GoogleMapsAddressAutocomplete
            value={sanitizeInputValue(addressData.search)}
            onChange={handleSearchChange}
            onPlaceSelect={handlePlaceSelect}
            placeholder={placeholder}
            disabled={disabled}
            className=""
          />
          {helpText && (
            <p className="text-xs text-zinc-500">{helpText}</p>
          )}
        </div>

        {/* Campi indirizzo manuali */}
        <div className="space-y-4">
          <div className="text-sm font-medium text-zinc-400">Oppure compila manualmente:</div>
          {/* Prima riga: Via e Numero Civico */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-zinc-300 mb-1">
                Via/Indirizzo <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={sanitizeInputValue(addressData.via)}
                onChange={(e) => handleFieldChange('via', e.target.value)}
                onBlur={handleFieldBlur}
                placeholder="Via, Viale, Piazza..."
                disabled={disabled}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors bg-zinc-800 text-white placeholder-zinc-400 !border-zinc-600 focus:!border-zinc-500 focus:!ring-zinc-500"
                tabIndex={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">
                Numero Civico
              </label>
              <input
                type="text"
                value={sanitizeInputValue(addressData.numeroCivico)}
                onChange={(e) => handleFieldChange('numeroCivico', e.target.value)}
                onBlur={handleFieldBlur}
                placeholder="N°"
                disabled={disabled}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors bg-zinc-800 text-white placeholder-zinc-400 !border-zinc-600 focus:!border-zinc-500 focus:!ring-zinc-500"
                tabIndex={3}
              />
            </div>
          </div>
          {/* Seconda riga: Città e CAP */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">
                Città <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={sanitizeInputValue(addressData.citta)}
                onChange={(e) => handleFieldChange('citta', e.target.value)}
                onBlur={handleFieldBlur}
                placeholder="Città"
                disabled={disabled}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors bg-zinc-800 text-white placeholder-zinc-400 !border-zinc-600 focus:!border-zinc-500 focus:!ring-zinc-500"
                tabIndex={4}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">
                CAP <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={sanitizeInputValue(addressData.cap)}
                onChange={(e) => handleFieldChange('cap', e.target.value)}
                onBlur={handleFieldBlur}
                placeholder="00000"
                disabled={disabled}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors bg-zinc-800 text-white placeholder-zinc-400 !border-zinc-500 focus:!ring-zinc-500"
                tabIndex={5}
              />
            </div>
          </div>
          {/* Terza riga: Provincia e Paese */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">
                Provincia <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={sanitizeInputValue(addressData.provincia)}
                onChange={(e) => handleFieldChange('provincia', e.target.value)}
                onBlur={handleFieldBlur}
                placeholder="CT, PA, MI..."
                disabled={disabled}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors bg-zinc-800 text-white placeholder-zinc-400 !border-zinc-600 focus:!border-zinc-500 focus:!ring-zinc-500"
                tabIndex={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">
                Paese
              </label>
              <input
                type="text"
                value={sanitizeInputValue(addressData.paese)}
                onChange={(e) => handleFieldChange('paese', e.target.value)}
                onBlur={handleFieldBlur}
                placeholder="Italia"
                disabled={disabled}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors bg-zinc-800 text-white placeholder-zinc-400 !border-zinc-600 focus:!border-zinc-500 focus:!ring-zinc-500"
                tabIndex={7}
              />
            </div>
          </div>
        </div>

        {/* Mappa */}
        <div className="mt-6">
          <InteractiveMap
            lat={addressData.lat}
            lng={addressData.lng}
            disabled={disabled}
          />
        </div>

        {/* Calcolo tempo di percorrenza */}
        <TravelTimeCalculator
          originLat={addressData.lat}
          originLng={addressData.lng}
          disabled={disabled}
        />

        {/* Errori di validazione */}
        {Object.keys(fieldErrors).length > 0 && (
          <div className="text-xs text-red-400 mt-2">
            Compila tutti i campi obbligatori o usa il campo di ricerca
          </div>
        )}
        
        {hasError && errorMessage && (
          <div className="text-xs text-red-400 mt-2">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

// Utility function per debounce
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default AddressSection; 